/**
 * dashboardRoutePaths — reine, FIREBASE-FREIE Datenliste aller `/dashboard/*`-
 * Routen-Pfade (M-UX-1.1, `architecture-ux-restructure.md` §4.2).
 *
 * **Bau-Auflage Testbarkeit (VERBINDLICH):** Ein voller `App.tsx`-Render (bzw.
 * jeder Import, der transitiv `App.tsx`/`firebaseConfig.ts` zieht) ist in Jest
 * NICHT möglich — `firebaseConfig.ts` wirft ohne echtes `.env`
 * `auth/invalid-api-key`. Auch `buildDashboardChildren()`
 * (`routes/dashboardChildren.tsx`) selbst zieht über `element: <Page/>`-
 * Referenzen transitiv Firebase und ist damit im Test NICHT importierbar.
 *
 * Dieses Modul importiert daher AUSSCHLIESSLICH pure Config-Module
 * (`config/*Access.ts` — keine davon importiert Firebase) und `process.env`.
 * KEINE Page-Komponenten, KEIN `createBrowserRouter`/`RouterProvider`, KEIN
 * `firebaseConfig`. Der Registry-Test (`navigation.test.ts`) importiert NUR
 * `NAV_TREE`/`NAV_ACTIONS` (`config/navigation.ts`, ebenfalls firebase-frei)
 * und `DASHBOARD_ROUTE_PATHS` aus DIESER Datei — nie `buildDashboardChildren`.
 *
 * `buildDashboardChildren()` (`routes/dashboardChildren.tsx`) leitet seine
 * element-tragenden `RouteObject[]` aus GENAU dieser Pfad-Liste ab (eine
 * Quelle) — Verhaltensidentität zu `App.tsx` bei erhaltener Testbarkeit.
 *
 * @module routes/dashboardRoutePaths
 */

import { INBOUND_ENABLED_FLAG, INBOUND_BETA_TENANT_UIDS } from '../config/inboundAccess';
import { RECEIPT_SCAN_FLAG, RECEIPT_SCAN_BETA_UIDS } from '../config/receiptScanAccess';
import { DATEV_EXPORT_FLAG, DATEV_EXPORT_BETA_UIDS } from '../config/datevExportAccess';
import { BANK_RECONCILE_FLAG, BANK_RECONCILE_BETA_UIDS } from '../config/bankReconcileAccess';
import { UNIFIED_INBOX_ENABLED } from '../config/unifiedInboxAccess';
import { TRAVEL_EXPENSES_FLAG, TRAVEL_EXPENSES_BETA_UIDS } from '../config/travelExpensesAccess';
import { USTVA_ROUTE_ENABLED } from '../config/ustvaAccess';
import { RECURRING_ROUTE_ENABLED } from '../config/recurringAccess';
import { GOBD_ROUTE_ENABLED } from '../config/gobdAccess';

// Feature flags — fail-closed, gespiegelt aus `App.tsx` (Route-Registrierungs-
// Bedingungen). Bewusste Duplikation statt Import aus App.tsx, weil App.tsx
// transitiv firebaseConfig zieht (s. Modul-Kopf) — Muster wie
// `bankReconcileAccess.ts` (Env-Var-Spiegelung statt Re-Import).
const EXPENSES_ENABLED = process.env.REACT_APP_EXPENSES_ENABLED === 'true';
// RECURRING/USTVA/GOBD-Route-Gates leben jetzt in ihren Access-Modulen
// (`config/recurringAccess.ts`/`ustvaAccess.ts`/`gobdAccess.ts`) als
// `X_ROUTE_ENABLED = X_FLAG || X_BETA_UIDS.length > 0` — Route registriert,
// sobald das Flag ODER die (hartkodierte) Beta-Allowlist greift (Muster
// `TRAVEL_EXPENSES_ROUTE_ENABLED`, s. u.). Da die Beta-Allowlisten nicht leer
// sind, sind diese drei Routen jetzt UNBEDINGT registriert; die feine
// Per-Nutzer-Entscheidung (wer die Seite sieht) trifft `canUseUstva`/
// `canUseRecurring`/`canUseGobd` im Nav-/Seiten-Gate (fail-closed gewahrt).
const INBOUND_ROUTE_ENABLED = INBOUND_ENABLED_FLAG || INBOUND_BETA_TENANT_UIDS.length > 0;
const DATEV_EXPORT_ROUTE_ENABLED = DATEV_EXPORT_FLAG || DATEV_EXPORT_BETA_UIDS.length > 0;
const RECEIPT_SCAN_ROUTE_ENABLED = RECEIPT_SCAN_FLAG || RECEIPT_SCAN_BETA_UIDS.length > 0;
const BANK_RECONCILE_ROUTE_ENABLED = BANK_RECONCILE_FLAG || BANK_RECONCILE_BETA_UIDS.length > 0;
// fix-unified-inbox-beta-gate: die Route-Registrierung bleibt (wie bei allen
// anderen Beta-Features hier) grob/env-basiert — registriert, sobald das
// globale Flag ODER EINER der beiden aggregierten Kanal-Betas (Inbound/
// Scanner) greifen könnte. Die feine Per-Nutzer-Entscheidung (wer die Seite
// wirklich sieht) trifft `canUseUnifiedInbox` per Render in
// `routes/dashboardChildren.tsx` (Nav-Sichtbarkeit + Route-Erreichbarkeit
// bleiben dadurch konsistent, ohne dass diese Firebase-freie Datei Zugriff
// auf den aktuellen Nutzer bräuchte).
const UNIFIED_INBOX_ROUTE_ENABLED =
  UNIFIED_INBOX_ENABLED || INBOUND_BETA_TENANT_UIDS.length > 0 || RECEIPT_SCAN_BETA_UIDS.length > 0;
// RK-7 (architecture-reisekosten.md §8): identisches Muster wie die anderen
// Beta-Features oben — Route ist registriert, sobald das Flag ODER die
// (hartkodierte) Beta-Allowlist greift, unabhängig vom Nav-Eintrag (Auflage 6).
const TRAVEL_EXPENSES_ROUTE_ENABLED = TRAVEL_EXPENSES_FLAG || TRAVEL_EXPENSES_BETA_UIDS.length > 0;

/**
 * Alle heute REGISTRIERTEN `/dashboard/*`-Routen-Pfade (absolut, Index-Route
 * = `'/dashboard'`), unter Berücksichtigung der Flag-/Beta-Gates — identisch
 * zur `children`-Liste in `App.tsx` vor der M-UX-1.1-Extraktion. Wird zur
 * Modul-Ladezeit ausgewertet (wie `App.tsx` selbst), daher forciert der
 * Registry-Test die Flags VOR dem Import via `jest.isolateModules`.
 */
export const DASHBOARD_ROUTE_PATHS: readonly string[] = [
  '/dashboard',
  // architecture-cockpit-route-split.md §3 E1: die eigene Formular-Route ist
  // jetzt UNBEDINGT registriert (flag-frei), Präzedenz `/dashboard/cockpit`/
  // `/dashboard/mandanten`. Sie rendert immer das `InvoiceDashboard` und ist
  // das konstante Ziel von `INVOICE_FORM_ROUTE` (§3 E2) ⇒ „Rechnung erstellen"
  // ist in JEDEM Flag-/Beta-Zustand erreichbar, Self-Loop strukturell
  // unmöglich. Die frühere Konditionalität war eine Null-Delta-Vorsicht aus
  // M-UX-4.2 ohne technischen Grund (§3 E1).
  '/dashboard/rechnungen/neu',
  '/dashboard/archiv',
  '/dashboard/angebote-archiv',
  '/dashboard/angebote',
  '/dashboard/xrechnung',
  '/dashboard/abonnement',
  '/dashboard/abo-erfolg',
  '/dashboard/abo-abgebrochen',
  '/dashboard/konto',
  '/dashboard/konto/steuerberater',
  // M-UX-7.2: Mandanten-Übersicht — UNBEDINGT registriert (nicht flag-
  // konditional), wie `/dashboard/cockpit`: Deep-Link-/Bookmark-Erhalt; die
  // Zugriffssteuerung liegt im Nav-Prädikat (`config/navigation.ts`) + im
  // Server-Scope, nicht in der Routen-Registrierung.
  '/dashboard/mandanten',
  '/dashboard/cockpit',
  '/dashboard/datev-sammelexport',
  '/dashboard/konto/mandanten-einladen',
  '/dashboard/konto/kanzlei-zugang',
  '/dashboard/kunden',
  '/dashboard/produkte',
  '/dashboard/workflows',
  '/dashboard/workflows/:id',
  '/dashboard/pdf-design',
  '/dashboard/eingangsrechnungen',
  // M-UX-5.2: `/dashboard/eingaenge` (Unified Inbox) — registriert, sobald
  // IRGENDEIN Nutzer sie erreichen könnte (globales Flag ODER Inbound-/
  // Scanner-Beta-Allowlist, s. `UNIFIED_INBOX_ROUTE_ENABLED` oben). Fail-closed
  // bleibt gewahrt: die tatsächliche Sichtbarkeit je Nutzer entscheidet
  // `canUseUnifiedInbox` in `routes/dashboardChildren.tsx`.
  ...(UNIFIED_INBOX_ROUTE_ENABLED ? ['/dashboard/eingaenge'] : []),
  ...(INBOUND_ROUTE_ENABLED ? ['/dashboard/posteingang'] : []),
  '/dashboard/offene-posten',
  '/dashboard/nachrichten',
  ...(EXPENSES_ENABLED ? ['/dashboard/ausgaben'] : []),
  ...(RECURRING_ROUTE_ENABLED ? ['/dashboard/wiederkehrend'] : []),
  ...(DATEV_EXPORT_ROUTE_ENABLED ? ['/dashboard/datev-export'] : []),
  ...(USTVA_ROUTE_ENABLED ? ['/dashboard/umsatzsteuer'] : []),
  ...(GOBD_ROUTE_ENABLED ? ['/dashboard/gobd-export'] : []),
  // GoBD-Verfahrensdoku-Seite (Doku-Volltext) teilt sich das Gate mit dem
  // Export-Panel (`GOBD_ROUTE_ENABLED` = Flag ODER Beta-Allowlist, s.
  // config/gobdAccess.ts) — kein eigenes Flag.
  ...(GOBD_ROUTE_ENABLED ? ['/dashboard/gobd'] : []),
  ...(RECEIPT_SCAN_ROUTE_ENABLED ? ['/dashboard/belege'] : []),
  ...(BANK_RECONCILE_ROUTE_ENABLED ? ['/dashboard/bank-abgleich'] : []),
  // RK-7: Reisekosten-Seite, flag-/beta-konditional (architecture-reisekosten.md
  // §8). Erreichbarkeit im MVP ausschließlich per Deep-Link — s.
  // ROUTES_WITHOUT_NAV unten (Auflage 6, Nav-Eintrag folgt durch Nav-Team).
  ...(TRAVEL_EXPENSES_ROUTE_ENABLED ? ['/dashboard/reisekosten'] : []),
];

/**
 * Registrierte Routen OHNE Nav-Eintrag (§4.2 „Gegenrichtung als Warn-Liste").
 * Jeder Eintrag hier ist eine bewusste, dokumentierte Entscheidung — kein
 * vergessener Link:
 * - `xrechnung`: bleibt registriert (Bookmarks), aber ohne Nav-Eintrag
 *   (M-UX-0.1, Entscheidung „fixiert").
 * - `abo-erfolg`/`abo-abgebrochen`: Stripe-Checkout-Redirect-Ziele, nie ein
 *   Nav-Klickziel.
 * - `konto/kanzlei-zugang`: Token-Redirect-Ziel (WL-K5a), kein Nav-Klickziel.
 * - `workflows/:id`: Detail-Route, nur per Klick aus der `workflows`-Liste
 *   erreichbar, kein eigener Nav-Eintrag.
 * - `nachrichten`: hat einen Top-Bar-Link AUSSERHALB des Baums
 *   (`MainLayout.tsx`, `canUseMessaging`) — bleibt laut Architektur (§2.1
 *   Top-Bar-Zeile) dauerhaft außerhalb von `NAV_TREE`.
 * - `cockpit`/`datev-sammelexport`: M-UX-3.2 („Advisor-Baum") hat beide in
 *   eigene Nav-Sektionen umgezogen (`kanzlei`/`pruefung`, s.
 *   `config/navigation.ts`) — sie sind daher NICHT mehr in dieser Liste.
 * - `/dashboard`: Cockpit-Home (architecture-cockpit-route-split.md §5.3) —
 *   `/dashboard` ist NIE ein Orphan (kein Allowlist-Eintrag): das statisch
 *   vorhandene `overview`-Item (Sektion `uebersicht`, Logo-Ziel + oberster
 *   Sidebar-Eintrag) trägt die Route in JEDEM Zustand — `collectNavRoutes`
 *   sammelt Nav-Routen unabhängig von `visibility` (Registry-Invariante,
 *   `navigation.test.ts`). `createInvoice` zeigt NICHT mehr auf `/dashboard`,
 *   sondern konstant auf `/dashboard/rechnungen/neu` (`INVOICE_FORM_ROUTE`,
 *   §3 E2).
 * - `reisekosten`: NACHTRAG 2026-07-16 (Nav-Team): der Nav-Eintrag existiert
 *   inzwischen (`config/navigation.ts`, Sektion `eingaenge`, Item
 *   `travelExpenses`, `labelKey: 'travelExpenses'`, gated identisch zum
 *   Seiten-Gate über `canUseTravelExpenses`) — analog zum `/dashboard`-Bullet
 *   oben steht die Route deshalb NICHT mehr in dieser Allowlist. Ursprünglich
 *   (RK-7, architecture-reisekosten.md §8, Auflage 6) bewusst OHNE
 *   Nav-Eintrag ausgeliefert („Nav-Umbau läuft in separatem Team-Strang").
 */
export const ROUTES_WITHOUT_NAV: readonly string[] = [
  '/dashboard/xrechnung',
  '/dashboard/abo-erfolg',
  '/dashboard/abo-abgebrochen',
  '/dashboard/konto/kanzlei-zugang',
  '/dashboard/workflows/:id',
  '/dashboard/nachrichten',
];
